import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { convertReactAdminIdToHydraId, getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';

import { GatewaysListFilterChips } from './GatewaysListFilterChips';
import { GatewaysListFilterForm } from './GatewaysListFilterForm';

type GatewaysListFilterValues = {
  name: string | null;
  status: string;
  managerVerified: boolean | null;
  provider: string | null;
  alias: string | null;
  direction: string | null;
  pcidss: boolean | null;
  pushApi: boolean | null;
};

export const DEFAULT_VALUES = {
  name: null,
  status: '',
  managerVerified: null,
  provider: null,
  alias: null,
  direction: null,
  pcidss: null,
  pushApi: null,
};

export const GatewaysListFilter = (): JSX.Element => {
  const { filterValues } = useListContext();
  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<GatewaysListFilterValues>({
      mode: 'all',
      defaultValues: {
        name: filterValues.name || '',
        status: filterValues.status
          ? convertReactAdminIdToHydraId(filterValues.status, Resources.GatewayStatuses)
          : '',
        managerVerified: filterValues.managerVerified ?? null,
        provider: filterValues.provider || null,
        alias: filterValues.alias || null,
        direction: filterValues.direction || null,
        pcidss: filterValues.pcidss ?? null,
        pushApi: filterValues.pushApi ?? null,
      },
      resetValues: DEFAULT_VALUES,
    });

  useSyncFilter({
    appliedFilters,
    transform: (appliedFilters) => {
      const { status, ...restFilters } = appliedFilters;
      return cleanEmpty(
        {
          ...restFilters,
          status: getId(status),
        },
        false,
      );
    },
  });

  const isFiltersApplied = useMemo(() => Object.keys(filterValues).length > 0, [filterValues]);

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <GatewaysListFilterForm />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters} isSkipBooleanValues={false}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <GatewaysListFilterChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isFiltersApplied}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
